/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

import { getUrlWithVariant, ProductForm } from '@shopify/theme-product-form';
import { formatMoney } from '@shopify/theme-currency';
import { register } from '@shopify/theme-sections';
import { forceFocus } from '@shopify/theme-a11y';
import { getNodeindex } from '../utilities/utilities';

const classes = {
  hide: 'hide',
};

const keyboardKeys = {
  ENTER: 13,
};

const selectors = {
  colorInput: '.color-input',
  colorSwatch: '.color-label',
  colorTooltip: '.color-name',
  swatchWrapper: '.product-form__swatch-wrapper',
  submitButton: '[data-submit-button]',
  submitButtonText: '[data-submit-button-text]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  priceWrapper: '[data-price-wrapper]',
  imageWrapper: '[data-product-image-wrapper]',
  visibleImageWrapper: `[data-product-image-wrapper]:not(.${classes.hide})`,
  imageWrapperById: (id) => `${selectors.imageWrapper}[data-image-id='${id}']`,
  productForm: '[data-product-form]',
  productPrice: '[data-product-price]',
  thumbnail: '[data-product-single-thumbnail]',
  thumbnailById: (id) => `[data-thumbnail-id='${id}']`,
  thumbnailActive: '[data-product-single-thumbnail][aria-current]',
  variantSelect: '[data-variant-select]',
};

const clearTooltip = function () {
  const colorInput = this.querySelector(selectors.colorInput);
  colorInput.blur();
  this.removeEventListener('mouseout', clearTooltip);
};

const hideOptionsFromSerialize = () => {
  const productOptionInputs = document.querySelectorAll('[name^=options]');

  for (let i = 0; i < productOptionInputs.length; i++) {
    productOptionInputs[i].setAttribute('name', '');
  }
};

const handleSwatchClick = function (event) {
  const wrapper = event.target.closest(selectors.swatchWrapper);

  wrapper.addEventListener('mouseout', clearTooltip);
};

const handleFormSubmit = (event) => {
  event.preventDefault();
  const form = event.target;
  const variantSelect = document.querySelector(selectors.variantSelect);

  if (!variantSelect.getAttribute('name')) {
    hideOptionsFromSerialize();
  }

  form.submit();
};

document.addEventListener('DOMContentLoaded', () => {
  const colorSwatches = document.querySelectorAll(selectors.colorSwatch);
  const productForm = document.querySelector(selectors.productForm);
  const variantSelect = document.querySelector(selectors.variantSelect);

  for (let i = 0; i < colorSwatches.length; i++) {
    colorSwatches[i].addEventListener('click', handleSwatchClick);
  }

  if (!productForm) {
    return;
  }

  // Making ReCharge Work!
  try {
    const variantId = variantSelect.value;
    const rechargeContainer = document.querySelector('#rc_container');
    productForm.addEventListener('submit', handleFormSubmit);

    const handleRechargeLoad = () => {
      if (!variantSelect) {
        return;
      }

      setTimeout(() => {
        variantSelect.value = variantId;
        variantSelect.dispatchEvent(new Event('change'));
      }, 400);
    };

    if (rechargeContainer) {
      // Begin ReCharge mutation listeners
      // Removes name attribute from option inputs so that serialized form doesn't add non-Recharge products
      const mutationConfig = {
        attributes: true,
      };

      const mutationCallback = (mutationsList, observer) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'attributes') {
            if (mutation.attributeName === 'style') {
              handleRechargeLoad();
            }
          }
        }
      };

      // Create an observer instance linked to the callback function
      const observer = new MutationObserver(mutationCallback);
      observer.observe(rechargeContainer, mutationConfig);
    }
  } catch (e) {
    Rollbar.error('Error in RechargeLoad product scripts', e);
  }
});

register('product', {
  async onLoad() {
    const productFormElement = document.querySelector(selectors.productForm);

    this.product = await this.getProductJson(productFormElement.dataset.productHandle);
    this.productForm = new ProductForm(productFormElement, this.product, {
      onOptionChange: this.onFormOptionChange.bind(this),
    });

    this.onThumbnailClick = this.onThumbnailClick.bind(this);
    this.onThumbnailKeyup = this.onThumbnailKeyup.bind(this);

    this.container.addEventListener('click', this.onThumbnailClick);
    this.container.addEventListener('keyup', this.onThumbnailKeyup);
  },

  onUnload() {
    this.productForm.destroy();
    this.removeEventListener('click', this.onThumbnailClick);
    this.removeEventListener('keyup', this.onThumbnailKeyup);
  },

  getProductJson(handle) {
    return fetch(`/products/${handle}.js`).then((response) => {
      return response.json();
    });
  },

  onFormOptionChange(event) {
    try {
      const variant = event.dataset.variant;

      this.renderImages(variant);
      this.renderPrice(variant);
      this.renderComparePrice(variant);
      this.renderSubmitButton(variant);

      this.updateVariantSelect(variant);
      this.updateBrowserHistory(variant);
    } catch (e) {
      Rollbar.error('Error in onFormOptionChange', e);
    }
  },

  updateVariantSelect(variant) {
    try {
      const variantSelect = document.querySelector(selectors.variantSelect);

      if (!variant) return;
      variantSelect.value = variant.id;
    } catch (e) {
      Rollbar.error('Error in updateVariantSelect', e);
    }
  },

  onThumbnailClick(event) {
    const thumbnail = event.target.closest(selectors.thumbnail);

    if (!thumbnail) {
      return;
    }

    event.preventDefault();

    this.renderFeaturedImage(thumbnail.dataset.thumbnailId);
    this.renderActiveThumbnail(thumbnail.dataset.thumbnailId);
  },

  onThumbnailKeyup(event) {
    if (event.keyCode !== keyboardKeys.ENTER || !event.target.closest(selectors.thumbnail)) {
      return;
    }

    const visibleFeaturedImageWrapper = this.container.querySelector(selectors.visibleImageWrapper);

    forceFocus(visibleFeaturedImageWrapper);
  },

  renderSubmitButton(variant) {
    const submitButton = this.container.querySelector(selectors.submitButton);
    const submitButtonText = this.container.querySelector(selectors.submitButtonText);

    if (!variant) {
      submitButton.disabled = true;
      submitButtonText.innerText = theme.strings.unavailable;
    } else if (variant.available) {
      submitButton.disabled = false;
      submitButtonText.innerText = theme.strings.addToCart;
    } else {
      submitButton.disabled = true;
      submitButtonText.innerText = theme.strings.soldOut;
    }
  },

  renderImages(variant) {
    if (!variant || variant.featured_image === null) {
      return;
    }

    this.renderFeaturedImage(variant.featured_image.id);
    // this.renderActiveThumbnail(variant.featured_image.id);
  },

  renderPrice(variant) {
    const priceElement = this.container.querySelectorAll(selectors.productPrice);
    const priceWrapperElement = this.container.querySelector(selectors.priceWrapper);

    priceWrapperElement.classList.toggle(classes.hide, !variant);

    if (variant) {
      priceElement.forEach((price) => {
        price.innerText = formatMoney(variant.price, '${{ amount }}'); // eslint-disable-line no-template-curly-in-string
      });
    }
  },

  renderComparePrice(variant) {
    if (!variant) {
      return;
    }

    const comparePriceElement = this.container.querySelector(selectors.comparePrice);
    const compareTextElement = this.container.querySelector(selectors.comparePriceText);

    if (!comparePriceElement || !compareTextElement) {
      return;
    }

    if (variant.compare_at_price > variant.price) {
      comparePriceElement.innerText = formatMoney(variant.compare_at_price, theme.moneyFormat);
      compareTextElement.classList.remove(classes.hide);
      comparePriceElement.classList.remove(classes.hide);
    } else {
      comparePriceElement.innerText = '';
      compareTextElement.classList.add(classes.hide);
      comparePriceElement.classList.add(classes.hide);
    }
  },

  renderActiveThumbnail(id) {
    const activeThumbnail = this.container.querySelector(selectors.thumbnailById(id));
    const inactiveThumbnail = this.container.querySelector(selectors.thumbnailActive);

    if (activeThumbnail === inactiveThumbnail) {
      return;
    }

    inactiveThumbnail.removeAttribute('aria-current');
    activeThumbnail.setAttribute('aria-current', true);
  },

  renderFeaturedImage(id) {
    const selectedImage = this.container.querySelector(selectors.imageWrapperById(id));
    const selectedSlide = selectedImage.closest('.slide');
    const slideIndex = getNodeindex(selectedSlide);

    if (!window.flkty) return;
    window.flkty.select(slideIndex);
  },

  updateBrowserHistory(variant) {
    const enableHistoryState = this.productForm.element.dataset.enableHistoryState;

    if (!variant || enableHistoryState !== 'true') {
      return;
    }

    const url = getUrlWithVariant(window.location.href, variant.id);
    window.history.replaceState({ path: url }, '', url);
  },
});
